import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Save, Loader2, Calendar, TrendingDown,
  Info, RefreshCw, Check, Plus, Trash2, CalendarRange,
  Sun, Snowflake, Star
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';

const TieredPricing = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Day prices state (1-30)
  const [dayPrices, setDayPrices] = useState({});
  const [extraDayPrice, setExtraDayPrice] = useState(7);
  
  // Quick fill
  const [quickFillStart, setQuickFillStart] = useState(1);
  const [quickFillEnd, setQuickFillEnd] = useState(30);
  const [quickFillPrice, setQuickFillPrice] = useState(12);

  // Special date ranges (temporadas)
  const [specialPeriods, setSpecialPeriods] = useState([]);
  const [newPeriod, setNewPeriod] = useState({
    name: '',
    startDate: '',
    endDate: '',
    priceModifier: 'fixed', // 'fixed', 'percentage', 'add'
    value: ''
  });

  const fetchPricing = async () => {
    try {
      const response = await adminApi.getPricing();
      const data = response.data;
      setPricing(data);
      
      // Initialize day prices from config or defaults
      const existingDayPrices = data.day_prices || {};
      const initialPrices = {};
      for (let i = 1; i <= 30; i++) {
        initialPrices[i] = existingDayPrices[String(i)] 
          ? existingDayPrices[String(i)] / 100 
          : 12; // Default 12€
      }
      setDayPrices(initialPrices);
      setExtraDayPrice((data.extra_day_price_cents || 700) / 100);
      
      // Load special periods
      if (data.special_periods) {
        setSpecialPeriods(data.special_periods);
      }
    } catch (error) {
      toast.error('Error al cargar configuración de precios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const handleDayPriceChange = (day, value) => {
    const numValue = parseFloat(value) || 0;
    setDayPrices(prev => ({
      ...prev,
      [day]: numValue
    }));
  };

  const handleQuickFill = () => {
    const start = Math.max(1, Math.min(30, quickFillStart));
    const end = Math.max(1, Math.min(30, quickFillEnd));
    
    if (start > end) {
      toast.error('El día inicial debe ser menor que el final');
      return;
    }
    
    const newPrices = { ...dayPrices };
    for (let i = start; i <= end; i++) {
      // Progressive pricing: base price + increment per day
      const increment = (i - start) * 3; // +3€ per additional day
      newPrices[i] = quickFillPrice + increment;
    }
    setDayPrices(newPrices);
    toast.success(`Precios progresivos aplicados: ${quickFillPrice}€ (${start} día) → ${quickFillPrice + (end - start) * 3}€ (${end} días)`);
  };

  const handleDecreasingFill = () => {
    // Progressive pricing: start from base price and add 3€ per day
    const basePrice = parseFloat(quickFillPrice) || 27;
    const incrementPerDay = 3;
    
    const newPrices = {};
    for (let i = 1; i <= 30; i++) {
      newPrices[i] = basePrice + (incrementPerDay * (i - 1));
    }
    setDayPrices(newPrices);
    const finalPrice = basePrice + (incrementPerDay * 29);
    toast.success(`Precios progresivos: 1 día=${basePrice}€ → 30 días=${finalPrice}€ (+${incrementPerDay}€/día)`);
  };

  // Special periods functions
  const addSpecialPeriod = () => {
    if (!newPeriod.name || !newPeriod.startDate || !newPeriod.endDate || !newPeriod.value) {
      toast.error('Completa todos los campos del período');
      return;
    }
    
    const start = parseISO(newPeriod.startDate);
    const end = parseISO(newPeriod.endDate);
    
    if (!isValid(start) || !isValid(end)) {
      toast.error('Fechas no válidas');
      return;
    }
    
    if (start > end) {
      toast.error('La fecha inicial debe ser anterior a la final');
      return;
    }
    
    const period = {
      id: Date.now().toString(),
      name: newPeriod.name,
      startDate: newPeriod.startDate,
      endDate: newPeriod.endDate,
      priceModifier: newPeriod.priceModifier,
      value: parseFloat(newPeriod.value)
    };
    
    setSpecialPeriods(prev => [...prev, period]);
    setNewPeriod({
      name: '',
      startDate: '',
      endDate: '',
      priceModifier: 'fixed',
      value: ''
    });
    toast.success(`Período "${period.name}" añadido`);
  };

  const removeSpecialPeriod = (id) => {
    setSpecialPeriods(prev => prev.filter(p => p.id !== id));
    toast.success('Período eliminado');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert prices to cents
      const dayPricesCents = {};
      for (let i = 1; i <= 30; i++) {
        dayPricesCents[String(i)] = Math.round((dayPrices[i] || 0) * 100);
      }
      
      const updatedPricing = {
        ...pricing,
        pricing_mode: 'TIERED',
        day_prices: dayPricesCents,
        extra_day_price_cents: Math.round(extraDayPrice * 100),
        special_periods: specialPeriods
      };
      
      await adminApi.updatePricing(updatedPricing);
      setPricing(updatedPricing);
      toast.success('Configuración de precios guardada');
    } catch (error) {
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const calculateTotalForDays = (numDays) => {
    // Fixed price per duration (not cumulative)
    if (numDays <= 30) {
      return (dayPrices[numDays] || 0).toFixed(2);
    } else {
      // For stays > 30 days: price for 30 days + extra days
      const basePrice = dayPrices[30] || 0;
      const extraDays = numDays - 30;
      const total = basePrice + (extraDays * extraDayPrice);
      return total.toFixed(2);
    }
  };

  const formatDateRange = (start, end) => {
    try {
      const startDate = parseISO(start);
      const endDate = parseISO(end);
      return `${format(startDate, 'dd MMM', { locale: es })} - ${format(endDate, 'dd MMM yyyy', { locale: es })}`;
    } catch {
      return `${start} - ${end}`;
    }
  };

  const getModifierText = (modifier, value) => {
    switch (modifier) {
      case 'fixed':
        return `${value}€/día`;
      case 'percentage':
        return `+${value}%`;
      case 'add':
        return `+${value}€/día`;
      default:
        return value;
    }
  };

  const getSeasonIcon = (name) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('verano') || nameLower.includes('summer') || nameLower.includes('agosto')) {
      return <Sun className="w-4 h-4 text-yellow-400" />;
    }
    if (nameLower.includes('navidad') || nameLower.includes('christmas') || nameLower.includes('invierno')) {
      return <Snowflake className="w-4 h-4 text-blue-400" />;
    }
    return <Star className="w-4 h-4 text-primary" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Calendar className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Tabla de Precios por Duración</h2>
            <p className="text-sm text-zinc-400">Configura el precio TOTAL para cada duración de estancia (1-30 días)</p>
          </div>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-primary hover:bg-primary/90 text-black font-medium"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar Cambios
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-300">
          <p className="font-medium mb-1">Cómo funciona:</p>
          <p className="text-blue-400">
            El precio del "Día X" es el <strong>precio TOTAL</strong> de una reserva de X días (no se suma).
            Ejemplo: Si "Día 7" = 45€, una reserva de 7 días costará 45€ en total.
            Las temporadas especiales pueden modificar estos precios.
          </p>
        </div>
      </div>

      {/* Quick Fill Tools */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-primary" />
          Herramientas de Llenado Rápido
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fill Range */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <Label className="text-zinc-300 text-sm mb-2 block">Llenar Rango con Precio Base (+3€/día)</Label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-zinc-500 text-sm">Desde</span>
                <Input 
                  type="number" 
                  min="1" 
                  max="30"
                  value={quickFillStart}
                  onChange={(e) => setQuickFillStart(parseInt(e.target.value) || 1)}
                  className="w-16 bg-zinc-700 border-zinc-600 text-white text-center"
                />
                <span className="text-zinc-500 text-sm">hasta</span>
                <Input 
                  type="number" 
                  min="1" 
                  max="30"
                  value={quickFillEnd}
                  onChange={(e) => setQuickFillEnd(parseInt(e.target.value) || 30)}
                  className="w-16 bg-zinc-700 border-zinc-600 text-white text-center"
                />
                <span className="text-zinc-500 text-sm">días</span>
              </div>
              <span className="text-zinc-500">=</span>
              <div className="relative">
                <Input 
                  type="number" 
                  step="0.5"
                  min="0"
                  value={quickFillPrice}
                  onChange={(e) => setQuickFillPrice(parseFloat(e.target.value) || 0)}
                  className="w-20 bg-zinc-700 border-zinc-600 text-white pr-6"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400">€</span>
              </div>
              <Button 
                onClick={handleQuickFill}
                size="sm"
                className="bg-zinc-700 hover:bg-zinc-600 text-white"
              >
                Aplicar
              </Button>
            </div>
          </div>
          
          {/* Progressive Fill */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <Label className="text-zinc-300 text-sm mb-2 block">Llenar Todos (1-30 días) con Precio Progresivo</Label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-zinc-500 text-sm">Base</span>
                <div className="relative">
                  <Input 
                    type="number" 
                    step="0.5"
                    min="5"
                    value={quickFillPrice}
                    onChange={(e) => setQuickFillPrice(parseFloat(e.target.value) || 27)}
                    className="w-20 bg-zinc-700 border-zinc-600 text-white pr-6"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400">€</span>
                </div>
              </div>
              <TrendingDown className="w-4 h-4 text-green-400 rotate-180" />
              <span className="text-zinc-500 text-sm">+3€/día</span>
              <Button 
                onClick={handleDecreasingFill}
                size="sm"
                className="bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/30"
              >
                <TrendingDown className="w-3 h-3 mr-1 rotate-180" />
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Price Table */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="text-white font-medium flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            Precio Total por Duración de Estancia (1-30 días)
          </h3>
          <p className="text-xs text-zinc-400 mt-1">El valor es el precio TOTAL de la reserva, no el precio por día</p>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
              <div 
                key={`day-${day}`} 
                className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50 hover:border-primary/30 transition-colors"
              >
                <Label className="text-xs text-zinc-400 mb-1 block">
                  {day} {day === 1 ? 'día' : 'días'}
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={dayPrices[day] || ''}
                    onChange={(e) => handleDayPriceChange(day, e.target.value)}
                    className="w-full bg-zinc-700 border-zinc-600 text-white text-right pr-6 h-9"
                    placeholder="0"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">€</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Extra Day Price */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              Precio por Día Extra (a partir del día 31)
            </h3>
            <p className="text-sm text-zinc-400 mt-1">
              Se añade este precio por cada día adicional a partir del día 31
            </p>
          </div>
          <div className="relative">
            <Input
              type="number"
              step="0.5"
              min="0"
              value={extraDayPrice}
              onChange={(e) => setExtraDayPrice(parseFloat(e.target.value) || 0)}
              className="w-24 bg-zinc-700 border-zinc-600 text-white text-right pr-6"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400">€</span>
          </div>
        </div>
      </div>

      {/* Special Periods / Temporadas */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="text-white font-medium flex items-center gap-2">
            <CalendarRange className="w-4 h-4 text-orange-400" />
            Temporadas Especiales (Precios por Fecha)
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            Define precios diferentes para temporadas específicas (ej: Verano, Navidad, Semana Santa)
          </p>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Add new period form */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-dashed border-zinc-600">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div className="md:col-span-2">
                <Label className="text-xs text-zinc-400 mb-1 block">Nombre del período</Label>
                <Input
                  type="text"
                  placeholder="Ej: Verano 2026, Agosto, Navidad..."
                  value={newPeriod.name}
                  onChange={(e) => setNewPeriod(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-zinc-700 border-zinc-600 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 mb-1 block">Fecha inicio</Label>
                <Input
                  type="date"
                  value={newPeriod.startDate}
                  onChange={(e) => setNewPeriod(prev => ({ ...prev, startDate: e.target.value }))}
                  className="bg-zinc-700 border-zinc-600 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 mb-1 block">Fecha fin</Label>
                <Input
                  type="date"
                  value={newPeriod.endDate}
                  onChange={(e) => setNewPeriod(prev => ({ ...prev, endDate: e.target.value }))}
                  min={newPeriod.startDate}
                  className="bg-zinc-700 border-zinc-600 text-white"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 mb-1 block">Tipo de precio</Label>
                <select
                  value={newPeriod.priceModifier}
                  onChange={(e) => setNewPeriod(prev => ({ ...prev, priceModifier: e.target.value }))}
                  className="w-full h-10 px-3 bg-zinc-700 border border-zinc-600 text-white rounded-md text-sm"
                >
                  <option value="fixed">Precio fijo</option>
                  <option value="add">Añadir €/día</option>
                  <option value="percentage">% adicional</option>
                </select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-zinc-400 mb-1 block">
                    {newPeriod.priceModifier === 'fixed' ? 'Precio €' : 
                     newPeriod.priceModifier === 'add' ? '+€/día' : '+%'}
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      placeholder={newPeriod.priceModifier === 'percentage' ? '20' : '15'}
                      value={newPeriod.value}
                      onChange={(e) => setNewPeriod(prev => ({ ...prev, value: e.target.value }))}
                      className="bg-zinc-700 border-zinc-600 text-white pr-6"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">
                      {newPeriod.priceModifier === 'percentage' ? '%' : '€'}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={addSpecialPeriod}
                  className="bg-primary hover:bg-primary/90 text-black mt-5"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* List of special periods */}
          {specialPeriods.length > 0 ? (
            <div className="space-y-2">
              {specialPeriods.map((period) => (
                <div 
                  key={period.id}
                  className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50 hover:border-primary/20"
                >
                  <div className="flex items-center gap-3">
                    {getSeasonIcon(period.name)}
                    <div>
                      <p className="text-white font-medium">{period.name}</p>
                      <p className="text-xs text-zinc-400">
                        {formatDateRange(period.startDate, period.endDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      period.priceModifier === 'fixed' 
                        ? 'bg-primary/20 text-primary' 
                        : period.priceModifier === 'percentage'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-green-500/20 text-green-400'
                    }`}>
                      {getModifierText(period.priceModifier, period.value)}
                    </span>
                    <button
                      onClick={() => removeSpecialPeriod(period.id)}
                      className="text-zinc-500 hover:text-red-400 transition-colors p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <CalendarRange className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No hay temporadas especiales configuradas</p>
              <p className="text-xs mt-1">Añade períodos como Verano o Navidad con precios diferentes</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Calculator */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <Check className="w-4 h-4 text-green-400" />
          Previsualización de Precios (sin temporadas especiales)
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {[3, 5, 7, 10, 14, 21, 30, 35, 45, 60].map((days) => (
            <div key={`preview-${days}`} className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <p className="text-xs text-zinc-400 mb-1">{days} días</p>
              <p className="text-lg font-bold text-primary">{calculateTotalForDays(days)}€</p>
              <p className="text-xs text-zinc-500">
                {(parseFloat(calculateTotalForDays(days)) / days).toFixed(2)}€/día
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Active Mode Indicator */}
      {(pricing?.pricing_mode === 'TIERED' || pricing?.pricing_mode === 'ADVANCED') && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-400" />
          <p className="text-green-300 text-sm">
            <span className="font-medium">Modo activo:</span> Precios por día de estancia (AVANZADO)
            {specialPeriods.length > 0 && (
              <span className="ml-2 text-green-400">+ {specialPeriods.length} temporada(s) especial(es)</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default TieredPricing;
